import {
  ReservationHistoryAction,
  ReservationHistoryActionLabel,
  ReservationStatus
} from '@idc-forms/types/reservation'
import type { IProject } from '@/features/projects/models/project'
import type { ITypeProperty } from '@/features/type-properties/models/type-property'

export { ReservationHistoryAction, ReservationHistoryActionLabel, ReservationStatus }

export enum InventoryStatus {
  AVAILABLE = 'AVAILABLE',
  RESERVED = 'RESERVED',
  IN_NEGOTIATION = 'IN_NEGOTIATION',
  PENDING_AUTHORIZATION = 'PENDING_AUTHORIZATION',
  SOLD = 'SOLD'
}

export const INVENTORY_STATUS_LABELS: Record<InventoryStatus, string> = {
  [InventoryStatus.AVAILABLE]: 'Disponible',
  [InventoryStatus.RESERVED]: 'Apartado',
  [InventoryStatus.IN_NEGOTIATION]: 'En Negociación',
  [InventoryStatus.PENDING_AUTHORIZATION]: 'Por Autorizar',
  [InventoryStatus.SOLD]: 'Vendida'
}

export const INVENTORY_STATUS_COLORS: Record<InventoryStatus, string> = {
  [InventoryStatus.AVAILABLE]:
    'bg-[#DFF9F3] text-[#008A76] dark:bg-[#008A76]/30 dark:text-[#DFF9F3]',
  [InventoryStatus.RESERVED]:
    'bg-[#FEEDD5] text-[#D14E00] dark:bg-[#D14E00]/30 dark:text-[#FEEDD5]',
  [InventoryStatus.IN_NEGOTIATION]:
    'bg-[#E1F6FD] text-[#0A76A0] dark:bg-[#0A76A0]/30 dark:text-[#E1F6FD]',
  [InventoryStatus.PENDING_AUTHORIZATION]:
    'bg-[#F9E2FF] text-[#9952AC] dark:bg-[#9952AC]/30 dark:text-[#F9E2FF]',
  [InventoryStatus.SOLD]: 'bg-[#FFE2E2] text-[#CB3001] dark:bg-[#CB3001]/30 dark:text-[#FFE2E2]'
}

export interface IInventoryItem {
  _id: string
  /**
   * El `prp_` del inmueble, cuando el payload v1 ya lo trae.
   *
   * OPCIONAL a propósito, igual que en el schema: un inmueble anterior al backfill no tiene uno,
   * y quien traduzca a v2 tiene que FALLAR —o quedarse en v1— en vez de mandar `undefined`.
   */
  publicId?: string
  code: string
  name: string
  availabilityStatus: InventoryStatus
  type_property?: ITypeProperty
  project?: IProject
  reservedBy?: {
    _id: string
    fullname: string
    avatar?: string
  } | null
  reservedDates?: string[]
  expiresAt?: string | null
  reservationId?: string | null
  /**
   * El `rsv_` de la reserva viva, cuando el payload lo trae.
   *
   * OPCIONAL como el `publicId` del inmueble y por el mismo motivo: una reserva creada por v1 no
   * tiene uno. Los comandos del panel se quedan en v1 en vez de romperse.
   */
  reservationPublicId?: string | null
  negotiationAgent?: {
    _id: string
    fullname: string
  } | null
  inNegotiation: boolean
  active: boolean
  requiresAuthorization?: boolean
  constructionM2?: number
  terrainM2?: number
  createdAt?: string
  updatedAt?: string
}

export const INVENTORY_KEYS = {
  CODE: 'code',
  NAME: 'name',
  STATUS: 'availabilityStatus',
  TYPE_PROPERTY: 'type_property',
  PROJECT: 'project',
  RESERVED_BY: 'reservedBy',
  EXPIRES_AT: 'expiresAt',
  IN_NEGOTIATION: 'inNegotiation',
  ACTIVE: 'active'
} as const

export const INVENTORY_COLUMN_NAMES: Record<string, string> = {
  code: 'Código',
  name: 'Inmueble',
  availabilityStatus: 'Estado',
  type_property: 'Tipo',
  project: 'Proyecto',
  reservedBy: 'Responsable',
  expiresAt: 'Vence',
  active: 'Activo'
}

export interface IInventoryDetail extends IInventoryItem {
  isCurrentUserAuthorizer?: boolean
  terrainV2?: number
  terraceM2?: number
  parkingM2?: number
  property?: string
  folio?: string
  book?: string
  waterMeter?: string
  electricityMeter?: string
  iusiAmount?: number
  telephone?: string
  project?: IProject & {
    image?: string
    currency?: string
    society?: { _id: string; name: string } | null
  }
  notes?: string
}

export interface IAvailabilityMetrics {
  total: number
  available: number
  reserved: number
  pendingAuthorization: number
  inNegotiation: number
  sold: number
}

export interface IMonthlyActivity {
  reservationsCreated: number
  salesClosed: number
}

export interface ISoldByProject {
  projectName: string
  projectCode: string
  count: number
  percentage: number
}

export interface IReservation {
  _id: string
  property: string
  reservedBy: {
    _id: string
    fullname: string
    email: string
    avatar?: string
  }
  reservedDates: string[]
  status: ReservationStatus
  authorizationTokens: Array<{ token: string; authorizer: string }>
  notes: string
  authorizedAt?: string
  authorizationNotes: string
  history: IReservationHistory[]
  createdAt: string
  updatedAt: string
}

export interface IReservationHistory {
  action: ReservationHistoryAction
  performedBy: IAuthorReservation | null
  notes: string
  performedAt: string
}

export interface IAuthorReservation {
  fullname: string
  email: string
  avatar?: string
}

export interface IListReservationHistory {
  _id: string
  history: IReservationHistory[]
  status: ReservationStatus
  reservedBy: IAuthorReservation
  notes?: string
  authorizationNotes?: string
  authorizedBy?: IAuthorReservation
  authorizedAt?: string
  releasedBy?: IAuthorReservation
  releasedAt?: string
  releaseNotes?: string
  createdAt: string
  updatedAt: string
  reservedDates: string[]
}

export interface IInventoryCommentAuthor {
  _id: string
  fullname: string
}

export interface IInventoryComment {
  _id: string
  body: string
  author: IInventoryCommentAuthor | null
  createdAt: string
  replies?: IInventoryComment[]
}
